/**
 * API Error Envelope Parser and Error Code Resolver
 * Implements standard error envelope mapping per Constitution Principle I & Contract.
 */

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    request_id?: string;
  };
}

const ERROR_MESSAGE_MAP: Record<string, string> = {
  AUTH_INVALID_CREDENTIALS: 'Incorrect email or password. Please try again.',
  AUTH_EMAIL_EXISTS: 'An account with this email address already exists.',
  AUTH_EXPIRED_TOKEN: 'Your session has expired. Please sign in again.',
  AUTH_REQUIRED: 'This store requires user authentication to view prices.',
  WATCH_LIMIT_EXCEEDED: 'You have reached your active watch limit. Please pause or remove an existing watch.',
  SSRF_REJECTED: 'This URL cannot be accessed for security and network safety reasons.',
  STORE_BLOCKED: 'This retailer is currently excluded from price tracking.',
  RATE_LIMITED: 'Too many requests. Please wait a moment before trying again.',
  VERIFICATION_FAILED: 'Could not extract product pricing from this URL. Please verify the link is a valid product page.',
  INTERNAL_SERVER_ERROR: 'An unexpected system error occurred. Please try again later.',
};

export function parseApiError(error: unknown): {
  code: string;
  message: string;
  details?: Record<string, unknown>;
} {
  if (!error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred. Please check your connection.',
    };
  }

  // Handle standard envelope { error: { code, message } }
  if (typeof error === 'object' && error !== null) {
    const maybeEnvelope = error as Record<string, any>;
    const errObj = maybeEnvelope['error'];
    if (errObj && typeof errObj === 'object') {
      const code = String(errObj['code'] || 'UNKNOWN_ERROR');
      const backendMessage = String(errObj['message'] || '');
      const message = ERROR_MESSAGE_MAP[code] || backendMessage || 'Operation failed.';
      return {
        code,
        message,
        details: errObj['details'],
      };
    }

    // Handle FastAPI detail response { detail: string | array }
    const detail = maybeEnvelope['detail'];
    if (detail) {
      if (typeof detail === 'string') {
        return {
          code: 'API_ERROR',
          message: detail,
        };
      }
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const msg = first && typeof first === 'object' && 'msg' in first ? String(first['msg']) : 'Invalid input parameters.';
        return {
          code: 'VALIDATION_ERROR',
          message: msg,
        };
      }
    }

    if ('message' in maybeEnvelope && typeof maybeEnvelope['message'] === 'string') {
      return {
        code: 'CLIENT_ERROR',
        message: maybeEnvelope['message'],
      };
    }
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: String(error),
  };
}
